import {
  Box,
  Button,
  Card,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { PersonAdd as PersonAddIcon } from '@mui/icons-material'
import { useForm } from '@tanstack/react-form'
import * as z from 'zod'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLightweightUsersV4,
  selectLightweightUsersV4,
} from 'src/v3/manage/manage.slice'
import {
  getZodValidationError,
  shouldShowZodFieldError,
} from 'src/v3/utilities/validation'
import { UserCapAlert } from 'src/v3/components/user-cap-alert'
import { isNil } from 'src/utilities/is-nil'
import { inviteLinkedServiceUser } from '../../utils/api'

interface UserManagementProps {
  linkedServiceID: string
  userCount: number
  userCap?: number
  isLoading: boolean
  onSuccess: (message: string) => void
  onError: (message: string) => void
}

export default function UserManagement({
  linkedServiceID,
  userCount,
  userCap,
  isLoading,
  onSuccess,
  onError,
}: UserManagementProps) {
  let dispatch = useAppDispatch()

  let linkedServiceUsers = useAppSelector(selectLightweightUsersV4)[
    linkedServiceID
  ]

  // Non-null when the user cap has been reached, carrying the cap value for display
  let reachedCap = !isNil(userCap) && userCount >= userCap ? userCap : null

  let inviteUserValidationSchema = z.object({
    email: z
      .email('Please enter a valid email address')
      .nonempty('Email is required')
      .refine((email) => {
        // Check if email already exists in linked service users
        if (!linkedServiceUsers) return true
        return !Object.values(linkedServiceUsers).some(
          (user) => user.primaryEmail.toLowerCase() === email.toLowerCase(),
        )
      }, 'This user has already been invited or is a member of the selected Linked Service'),
  })

  let noLinkedServiceSelected = !linkedServiceID || linkedServiceID === ''

  let form = useForm({
    defaultValues: {
      email: '',
    },
    validators: {
      onMount: inviteUserValidationSchema,
      onChange: inviteUserValidationSchema,
    },
    onSubmit: async ({ value }) => {
      if (noLinkedServiceSelected) return

      await inviteLinkedServiceUser(linkedServiceID, value.email)
        .then(() => dispatch(fetchLightweightUsersV4(linkedServiceID)))
        .then(() => {
          onSuccess(`Invite sent successfully to ${value.email}`)
          form.reset()
        })
        .catch((error: any) => {
          // eslint-disable-next-line no-console
          console.error('Error sending linked service user invite:', error)

          onError('Error sending invite')
        })
    },
  })

  return (
    <Card className="p-6" sx={{ bgcolor: 'background.paper' }}>
      <Typography
        variant="h6"
        component="h2"
        className="flex items-center gap-2 mb-4"
      >
        <PersonAddIcon />
        Invite User
      </Typography>

      {!isLoading && !isNil(reachedCap) && (
        <UserCapAlert userCap={reachedCap} />
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <form.Field name="email">
          {(field) => (
            <TextField
              fullWidth
              className="mb-4"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
              name={field.name}
              label="Email Address"
              placeholder="user@example.com"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              error={shouldShowZodFieldError(field.state.meta)}
              helperText={
                shouldShowZodFieldError(field.state.meta) &&
                getZodValidationError(field.state.meta.errors)
              }
              disabled={!isNil(reachedCap) || !linkedServiceID}
            />
          )}
        </form.Field>

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={
                !canSubmit ||
                !isNil(reachedCap) ||
                noLinkedServiceSelected ||
                isSubmitting
              }
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} />
                ) : (
                  <PersonAddIcon />
                )
              }
            >
              {isSubmitting ? 'Sending Invite...' : 'Send Invite'}
            </Button>
          )}
        </form.Subscribe>
      </form>

      {!isLoading && noLinkedServiceSelected && (
        <Alert severity="info" className="mt-4">
          Please select a linked service first
        </Alert>
      )}

      <Box className="mt-6">
        <Typography variant="h6" component="h2" className="mb-2">
          Bulk User Operations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Need to invite users in bulk? Contact support to request a bulk
          upload. To export your current user list, use the export option in the
          users table below.
        </Typography>
      </Box>
    </Card>
  )
}
